/**
 * ModuleTabs - Main Module Navigation Component
 * 
 * Provides top-level tab navigation for the major WMS modules:
 * - WMS (Warehouse Management System)
 * - OMS (Order Management System)
 * - TMS (Transport Management System)
 * - TTMS (Transport Trading Management System)
 * - CRM (Customer Relationship Management)
 * 
 * Aurora 2026 Design - Integrated with role-based access control
 */
import React, { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Tabs,
  Tab,
  Tooltip,
  useTheme,
  useMediaQuery,
  alpha
} from '@mui/material';
import {
  Warehouse as WMSIcon,
  ShoppingCart as OMSIcon,
  LocalShipping as TMSIcon,
  PriceChange as TTMSIcon,
  People as CRMIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext.helpers.js';

/**
 * Module configuration with routes and role permissions
 */
const MODULE_CONFIG = {
  admin: [
    {
      id: 'wms',
      labelKey: 'modules.wms',
      defaultLabel: 'WMS',
      fullLabelKey: 'modules.wmsFullName',
      fullDefaultLabel: 'Warehouse Management',
      icon: WMSIcon,
      color: '#4caf50',
      basePath: '/admin',
      routes: ['/admin/dashboard', '/admin/products', '/admin/inventory', '/admin/warehouses', '/admin/warehouse-3d', '/admin/picking-waves', '/admin/picking-lists', '/admin/workstation-monitoring']
    },
    {
      id: 'oms',
      labelKey: 'modules.oms',
      defaultLabel: 'OMS',
      fullLabelKey: 'modules.omsFullName',
      fullDefaultLabel: 'Order Management',
      icon: OMSIcon,
      color: '#2196f3',
      basePath: '/admin',
      routes: ['/admin/orders', '/admin/marketplace', '/admin/marketplace-connections']
    },
    {
      id: 'tms',
      labelKey: 'modules.tms',
      defaultLabel: 'TMS',
      fullLabelKey: 'modules.tmsFullName',
      fullDefaultLabel: 'Transport Management',
      icon: TMSIcon,
      color: '#e91e63',
      basePath: '/admin',
      routes: ['/admin/logistics', '/admin/shipping', '/admin/transport-management', '/admin/delivery-slot-management']
    },
    {
      id: 'ttms',
      labelKey: 'modules.ttms',
      defaultLabel: 'TTMS',
      fullLabelKey: 'modules.ttmsFullName',
      fullDefaultLabel: 'Transport Trading',
      icon: TTMSIcon,
      color: '#ff5722',
      basePath: '/admin',
      routes: ['/admin/ttms-trading', '/admin/financial']
    },
    {
      id: 'crm',
      labelKey: 'modules.crm',
      defaultLabel: 'CRM',
      fullLabelKey: 'modules.crmFullName',
      fullDefaultLabel: 'Customer Relations',
      icon: CRMIcon,
      color: '#9c27b0',
      basePath: '/admin',
      routes: ['/admin/customers', '/admin/workforce', '/admin/users', '/admin/user-management']
    }
  ],
  manager: [
    {
      id: 'wms',
      labelKey: 'modules.wms',
      defaultLabel: 'WMS',
      fullLabelKey: 'modules.wmsFullName',
      fullDefaultLabel: 'Warehouse Management',
      icon: WMSIcon,
      color: '#4caf50',
      basePath: '/manager',
      routes: ['/manager/dashboard', '/manager/products', '/manager/inventory', '/manager/warehouse', '/manager/warehouse-3d', '/manager/operations']
    },
    {
      id: 'oms',
      labelKey: 'modules.oms',
      defaultLabel: 'OMS',
      fullLabelKey: 'modules.omsFullName',
      fullDefaultLabel: 'Order Management',
      icon: OMSIcon,
      color: '#2196f3',
      basePath: '/manager',
      routes: ['/manager/orders', '/manager/backorder-queue']
    },
    {
      id: 'tms',
      labelKey: 'modules.tms',
      defaultLabel: 'TMS',
      fullLabelKey: 'modules.tmsFullName',
      fullDefaultLabel: 'Transport Management',
      icon: TMSIcon,
      color: '#e91e63',
      basePath: '/manager',
      routes: ['/manager/logistics', '/manager/tms']
    },
    {
      id: 'ttms',
      labelKey: 'modules.ttms',
      defaultLabel: 'TTMS',
      fullLabelKey: 'modules.ttmsFullName',
      fullDefaultLabel: 'Transport Trading',
      icon: TTMSIcon,
      color: '#ff5722',
      basePath: '/manager',
      routes: ['/manager/ttms-trading']
    },
    {
      id: 'crm',
      labelKey: 'modules.crm',
      defaultLabel: 'CRM',
      fullLabelKey: 'modules.crmFullName',
      fullDefaultLabel: 'Customer Relations',
      icon: CRMIcon,
      color: '#9c27b0',
      basePath: '/manager',
      routes: ['/manager/workforce', '/manager/returns', '/manager/returns-forecast', '/manager/cycle-count-management']
    }
  ],
  client: [
    {
      id: 'wms',
      labelKey: 'modules.wms',
      defaultLabel: 'WMS',
      fullLabelKey: 'modules.wmsFullName',
      fullDefaultLabel: 'Warehouse',
      icon: WMSIcon,
      color: '#4caf50',
      basePath: '/client',
      routes: ['/client/dashboard', '/client/products']
    },
    {
      id: 'oms',
      labelKey: 'modules.oms',
      defaultLabel: 'OMS',
      fullLabelKey: 'modules.omsFullName',
      fullDefaultLabel: 'Orders',
      icon: OMSIcon,
      color: '#2196f3',
      basePath: '/client',
      routes: ['/client/orders', '/client/oms', '/client/oms-dashboard', '/client/cms-integrations', '/client/parcel-sorting']
    },
    {
      id: 'tms',
      labelKey: 'modules.tms',
      defaultLabel: 'TMS',
      fullLabelKey: 'modules.tmsFullName',
      fullDefaultLabel: 'Shipping',
      icon: TMSIcon,
      color: '#e91e63',
      basePath: '/client',
      routes: ['/client/expeditions', '/client/tms']
    },
    {
      id: 'ttms',
      labelKey: 'modules.ttms',
      defaultLabel: 'TTMS',
      fullLabelKey: 'modules.ttmsFullName',
      fullDefaultLabel: 'Trading',
      icon: TTMSIcon,
      color: '#ff5722',
      basePath: '/client',
      routes: ['/client/ttms-trading', '/client/billing-estimate']
    },
    {
      id: 'crm',
      labelKey: 'modules.crm',
      defaultLabel: 'CRM',
      fullLabelKey: 'modules.crmFullName',
      fullDefaultLabel: 'Support',
      icon: CRMIcon,
      color: '#9c27b0',
      basePath: '/client',
      routes: ['/client/support-modern', '/client/profile', '/client/reports', '/client/workflows']
    }
  ]
};

/**
 * Get the primary role for module configuration
 */
const getPrimaryRole = (user) => {
  if (!user) return 'client';
  
  const roles = user.roles || (user.role ? [user.role] : []);
  
  if (roles.includes('admin')) return 'admin';
  if (roles.includes('manager')) return 'manager';
  if (roles.includes('client') || roles.some(r => r?.startsWith?.('client'))) return 'client';
  
  return 'client';
};

/**
 * ModuleTabs Component
 */
const ModuleTabs = () => {
  const { t } = useTranslation(['common', 'shared']);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const primaryRole = useMemo(() => getPrimaryRole(user), [user]);
  const modules = useMemo(() => MODULE_CONFIG[primaryRole] || MODULE_CONFIG.client, [primaryRole]);

  // Determine active module based on current route
  const activeModule = useMemo(() => {
    const currentPath = location.pathname;
    
    for (const module of modules) {
      if (module.routes.some(route => currentPath.startsWith(route))) {
        return module.id;
      }
    }
    
    // Default to first module if no match
    return modules[0]?.id || 'wms';
  }, [location.pathname, modules]);

  const handleModuleChange = (event, newValue) => {
    const selectedModule = modules.find(m => m.id === newValue);
    if (selectedModule) {
      // Navigate to the first route of the selected module
      navigate(selectedModule.routes[0]);
    }
  };

  if (!user) return null;

  return (
    <Box
      sx={{
        width: '100%',
        borderBottom: 1,
        borderColor: 'divider',
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)'
          : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        position: 'sticky',
        top: 64, // Below AppBar
        zIndex: 1100
      }}
    >
      <Tabs
        value={activeModule}
        onChange={handleModuleChange}
        variant={isMobile ? 'scrollable' : 'standard'}
        scrollButtons={isMobile ? 'auto' : false}
        centered={!isMobile}
        sx={{
          minHeight: 56,
          '& .MuiTabs-indicator': {
            height: 3,
            borderRadius: '3px 3px 0 0',
            background: modules.find(m => m.id === activeModule)?.color || theme.palette.primary.main
          },
          '& .MuiTabs-flexContainer': {
            justifyContent: isMobile ? 'flex-start' : 'center',
            gap: isTablet ? 0 : 1
          }
        }}
      >
        {modules.map((module) => {
          const IconComponent = module.icon;
          const isActive = activeModule === module.id;
          
          return (
            <Tab
              key={module.id}
              value={module.id}
              icon={
                <Tooltip 
                  title={t(module.fullLabelKey, module.fullDefaultLabel)} 
                  arrow 
                  placement="bottom"
                >
                  <IconComponent 
                    sx={{ 
                      fontSize: isTablet ? 20 : 22,
                      color: isActive ? module.color : 'text.secondary',
                      transition: 'all 0.2s ease'
                    }} 
                  />
                </Tooltip>
              }
              label={isMobile ? undefined : t(module.labelKey, module.defaultLabel)}
              iconPosition="start"
              sx={{
                minHeight: 56,
                minWidth: isMobile ? 60 : 100,
                px: isTablet ? 1.5 : 3,
                fontWeight: isActive ? 700 : 500,
                fontSize: isTablet ? '0.8rem' : '0.875rem',
                textTransform: 'none',
                color: isActive ? module.color : 'text.secondary',
                opacity: 1,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                borderRadius: '8px 8px 0 0',
                mx: 0.5,
                '&:hover': {
                  backgroundColor: alpha(module.color, 0.08),
                  color: module.color
                },
                '&.Mui-selected': {
                  color: module.color,
                  backgroundColor: alpha(module.color, 0.12)
                }
              }}
            />
          );
        })}
      </Tabs>
    </Box>
  );
};

export default React.memo(ModuleTabs);
